/* eslint-disable no-use-before-define */

'use strict';

import cacheUtil from '../utils/cache.js';

let selectors = {
    'body': 'body',
    'recaptchaToken': '.js-recaptcha-token',
    'footerCaptcha': '.js-footer-captcha'
};

let $cache = cacheUtil.generateCache(selectors);

const cssClasses = {
    'hidden': 'h-hidden'
};

let siteKey = (window.SitePreferences && window.SitePreferences.RECAPTCHA_V3_SITEKEY) || null;
if (!siteKey && $cache.recaptchaToken.length) {
    siteKey = $cache.recaptchaToken.data('sitekey');
}

let options = {
    'error': false,
    'readyCheck': null,
    'tokenTimeout': null,
    'resetTokenInterval': 120000,
    'readyCheckInterval': 1000,
    'sitekey': siteKey,
    'action': 'creditapplication'
};

/**
 * @function isReady
 * @description check if recaptcha is loaded
 * @returns {boolean} Boolean value to determine if grecaptcha is ready for use
 */
function isReady() {
    return typeof window !== 'undefined' &&
        typeof window.grecaptcha !== 'undefined' &&
        typeof window.grecaptcha.execute !== 'undefined';
}

/**
 * @function execute
 * @description load captcha
 * @param {boolean} isSkipCallback - Skip callback
 * @returns {void}
 */
function execute(isSkipCallback) {
    try {
        window.grecaptcha.execute(options.sitekey, { action: 'pageload' }).then(token => {
            $cache.recaptchaToken.val(token);

            if (options.onChangeCallback && !isSkipCallback) {
                options.onChangeCallback(token);
            }

            // Reset the token every 2 minutes to avoid recaptcha timeout error
            options.tokenTimeout = setInterval(
                resetToken,
                options.resetTokenInterval
            );
        });
    } catch (e) {
        $cache.recaptchaToken.val('');
        options.error = true;
    }
}

/**
 * @function resetToken
 * @description reloac captcha
 * @param {boolean} isSkipCallback - Skip callback
 * @returns {void}
 */
function resetToken(isSkipCallback) {
    clearInterval(options.tokenTimeout);
    $cache.recaptchaToken.val('');
    execute(isSkipCallback);
}

/**
 * @function updateReadyState
 * @description clear interval and load captcha
 * @returns {void}
 */
function updateReadyState() {
    if (isReady()) {
        clearInterval(options.readyCheck);
        execute();
    }
}

/**
 * @function loadReCaptcha
 * @param {Object} callback - callback function
 * @returns {void}
 */
function loadReCaptcha(callback) {
    if (!options.sitekey) {
        return;
    }

    options.onChangeCallback = callback;
    options.readyCheck = setInterval(updateReadyState, options.readyCheckInterval);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://www.google.com/recaptcha/api.js?render=${options.sitekey}`;
    script.async = true;
    $cache.body.append(script);
}

/**
 * @function init
 * @description initialize reCaptcha
 * @returns {void}
 */
function init() {
    if (
        (window.SitePreferences && window.SitePreferences.ADD_CAPTCHA_ANALYTICS) ||
        (
            $cache.recaptchaToken.length &&
            $cache.recaptchaToken.data('captcha-enabled')
        )
    ) {
        loadReCaptcha();
        $cache.footerCaptcha.removeClass(cssClasses.hidden);
    }
}

/**
 * @function formSubmit
 * @description executes google recaptcha and returns a promise
 * @returns {Object} Promise
 */
function formSubmit () {
    let promiseFunc = Promise.resolve();

    if (
        $cache.recaptchaToken.length &&
        $cache.recaptchaToken.data('captcha-enabled') &&
        window.grecaptcha
    ) {
        promiseFunc = window.grecaptcha.execute(
            $cache.recaptchaToken.data('sitekey'),
            {
                action: 'submit'
            }
        );
    }

    return promiseFunc;
}

export default {
    'loadReCaptcha': loadReCaptcha,
    'resetCaptchaToken': resetToken,
    'init': init,
    'formSubmit': formSubmit
};
